import AppCard from '@nxsys/components/AppCard';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppIconCard from '@nxsys/components/AppIconCard';
import AppInput from '@nxsys/components/AppInput';
import AppRateForm, { AppRateFormItem } from '@nxsys/components/AppRateForm';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import {
  InvoiceSystemTypeEnum,
  InvoiceTypeEnum,
} from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import type { FormProps } from 'antd';
import { Divider, Form, Typography } from 'antd';
import moment from 'moment';
import { forwardRef, Ref, useImperativeHandle, useMemo } from 'react';
import { useSelector } from 'react-redux';

export type InvoiceFormValues = {
  agencyRef?: Option<string>;
  companyRef?: Option<string>;
  invoicedDate?: string | null;
  endDate?: string | null;
  invoiceNo?: string;
  reference?: string;
  creditDate?: string | null;
  rates?: AppRateFormItem[];
};

export type InvoiceFormRef = {
  submit: () => void;
};

type InvoiceFormProps = {
  defaultValues?: Partial<InvoiceFormValues>;
  formRef?: Ref<InvoiceFormRef>;
  onSubmit?: (values: InvoiceFormValues) => void;
  isEdit?: boolean;
  invoiceType?: InvoiceTypeEnum;
  invoiceSystemType?: InvoiceSystemTypeEnum;
};

const innerDefaultValues = {
  agencyRef: null,
  companyRef: null,
  invoicedDate: moment().toISOString(),
  endDate: null,
  invoiceNo: '',
  reference: '',
  rates: [
    {
      description: '',
      net: '',
      rate: '',
      rateCode: null,
      rateId: '',
      rateType: '',
      total: '',
      units: '',
      vatDetails: null,
      vatRate: null,
    },
  ],
};

const InvoiceForm = forwardRef<InvoiceFormRef, InvoiceFormProps>(
  ({
    defaultValues = innerDefaultValues,
    onSubmit,
    formRef,
    isEdit = false,
    invoiceType = InvoiceTypeEnum.INVOICE,
  }, _ref) => {
    const { payrollCountry, isBureau } = useSelector(
      (state: AppState) => state.appStore,
    );

    const { messages } = useTypedIntl();
    const [form] = Form.useForm<InvoiceFormValues>();
    const watchEndDate = Form.useWatch('endDate', form);
    const watchInvoicedDate = Form.useWatch('invoicedDate', form);
    const watchRates = Form.useWatch('rates', form);

    useImperativeHandle(formRef, () => ({
      submit() {
        form.submit();
      },
    }));

    // Calculate totals from all rates using integer arithmetic (pence) to avoid floating point errors
    const totals = useMemo(() => {
      const rates = watchRates || [];

      const totalNetPence = rates.reduce((sum: number, rate: any) => {
        return sum + Math.round(parseFloat(rate.net || 0) * 100);
      }, 0);

      const totalVATPence = rates.reduce((sum: number, rate: any) => {
        const netPence = Math.round(parseFloat(rate.net || 0) * 100);
        const vatRate = parseFloat(rate.vatRate || 0);
        return sum + Math.round((netPence * vatRate) / 100);
      }, 0);

      return {
        net: formatMoney(totalNetPence / 100, showCurrencyByCountry(payrollCountry)),
        vat: formatMoney(totalVATPence / 100, showCurrencyByCountry(payrollCountry)),
        total: formatMoney((totalNetPence + totalVATPence) / 100, showCurrencyByCountry(payrollCountry)),
      };
    }, [watchRates]);

    const onFinish: FormProps<InvoiceFormValues>['onFinish'] = (values) => {
      onSubmit?.(values);
    };

    const onFinishFailed: FormProps<InvoiceFormValues>['onFinishFailed'] = () => {
      // noop - form validation handles error display
    };

    return (
      <div>
        <AppCard className="p-[16px]">
          <AppIconCard
            icon={<Icons.IconInvoice />}
            title={messages['invoices.invoicing']}
          />
          <Divider className="mt-[12px] mb-[16px]" />
          <AppFormContainer
            form={form}
            initialValues={defaultValues}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <div className="grid grid-cols-2 gap-x-4 max-sm:grid-cols-1">
              {!isBureau && (
                <Form.Item
                  label={messages['common.agencyName']}
                  name="agencyRef"
                  rules={[{ required: true }]}
                >
                  <AppSelectSync
                    placeholder={messages['form.selectAgency']}
                    apiType="searchAgency"
                    type="agency"
                    onChangeValue={(value: any) => {
                      form.setFieldsValue({ agencyRef: value });
                    }}
                    disabled={isEdit}
                  />
                </Form.Item>
              )}
              <Form.Item
                label={isBureau ? messages['common.bureau.companyName'] : messages['common.companyName']}
                name="companyRef"
                rules={[{ required: true }]}
              >
                <AppSelectSync
                  placeholder={messages['form.selectCompany']}
                  apiType="searchCompany"
                  type="company"
                  onChangeValue={(value: any) => {
                    form.setFieldsValue({ companyRef: value });
                  }}
                  onDataLoaded={(options) => {
                    if (!isEdit && options.length === 1) {
                      form.setFieldsValue({ companyRef: options[0] });
                    }
                  }}
                  disabled={isEdit}
                />
              </Form.Item>
              {invoiceType === InvoiceTypeEnum.INVOICE && (
                <Form.Item
                  label={messages['common.invoicedDate']}
                  name="invoicedDate"
                  rules={[{ required: true }]}
                >
                  <AppDatePicker
                    placeholder={messages['form.selectDate']}
                    format={'DD/MM/YYYY'}
                    maxDate={watchEndDate ? moment(watchEndDate) : undefined}
                    disabled={isEdit}
                  />
                </Form.Item>
              )}
              {invoiceType === InvoiceTypeEnum.INVOICE && (
                <Form.Item
                  label={messages['common.dueDate']}
                  name="endDate"
                  rules={[{ required: true }]}
                >
                  <AppDatePicker
                    placeholder={messages['form.selectDate']}
                    format={'DD/MM/YYYY'}
                    minDate={
                      watchInvoicedDate ? moment(watchInvoicedDate) : undefined
                    }
                    disabled={isEdit}
                  />
                </Form.Item>
              )}
              {invoiceType === InvoiceTypeEnum.CREDIT_NOTE && (
                <Form.Item
                  label={messages['common.crnDate']}
                  name="creditDate"
                  rules={[{ required: true }]}
                >
                  <AppDatePicker
                    placeholder={messages['form.selectDate']}
                    format={'DD/MM/YYYY'}
                    disabled={isEdit}
                  />
                </Form.Item>
              )}
              <Form.Item label={messages['common.reference']} name="reference">
                <AppInput
                  placeholder={messages['form.pleaseEnter']}
                  disabled={isEdit}
                />
              </Form.Item>
            </div>
          </AppFormContainer>
        </AppCard>

        <AppRateForm
          className="my-4"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          defaultValues={defaultValues}
          listDisabled={
            isEdit
              ? [
                  'net',
                  'rate',
                  'rateCode',
                  'rateType',
                  'total',
                  'units',
                  'vatDetails',
                  'vatRate',
                ]
              : []
          }
          disabledAdd={isEdit}
          disabledDelete={isEdit}
        />

        <div className="flex justify-end">
          <AppCard className="w-80 max-sm:w-full">
            <div className="space-y-3 p-4">
              <div className="flex justify-between items-center">
                <Typography.Text>Net</Typography.Text>
                <Typography.Text>{totals.net}</Typography.Text>
              </div>
              <div className="flex justify-between items-center">
                <Typography.Text>VAT</Typography.Text>
                <Typography.Text>{totals.vat}</Typography.Text>
              </div>
              <Divider />
              <div className="flex justify-between items-center">
                <Typography.Text>{messages['common.total']}</Typography.Text>
                <Typography.Text>{totals.total}</Typography.Text>
              </div>
            </div>
          </AppCard>
        </div>
      </div>
    );
  },
);

export default InvoiceForm;
